import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {getProduct} from '../services/api';
import {Product} from '../types/api';
import {Checkout} from '../components/Checkout';
import {Shows} from '../components/Shows';
import {SEO} from '../components/SEO';

export const ProductPage: React.FC = () => {
    const {refId = '1-day-pass'} = useParams<{ refId: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await getProduct(refId);
                setProduct(response.data);
            } catch (err) {
                setError('Failed to load product details');
                console.error('Error loading product:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [refId]);

    useEffect(() => {
        if (!refId) {
            navigate('/1-day-pass');
        }
    }, [refId, navigate]);

    if (loading) {
        return <div className="text-center py-8">Loading product details...</div>;
    }

    if (error || !product) {
        return <div className="text-center py-8 text-red-600">{error || 'Product not found'}</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <SEO
                productName={product.nameEn}
                price={product.price}
                description={`Get access to Viu Premium content with our ${product.nameEn}. Watch your favorite shows and movies in HD quality.`}
            />

            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <img src="/images/appvanz-logo.svg" alt="Appvanz Logo" className="h-16 sm:h-24"/>
                        <div className="text-right">
                            <p className="text-sm sm:text-base text-gray-600">Viu Authorized Reseller</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-8">
                    {/* Desktop Layout */}
                    <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12">
                        {/* Left Column: Product Details */}
                        <div className="lg:pr-12 lg:border-r">
                            <div className="text-center mb-8">
                                <a href="https://www.viu.com/" target="_blank" rel="noopener noreferrer"
                                   className="inline-block hover:opacity-90 transition-opacity">
                                    <img src="/images/viu-logo.svg" alt="Viu Logo" className="h-12 mx-auto mb-4"/>
                                </a>
                                <img src="/images/viu-vouchers.webp" alt="Viu Voucher Banner"
                                     className="w-full h-auto rounded-lg mb-6"/>
                                <h1 className="text-3xl font-bold text-gray-900">{product.nameEn}</h1>
                                <p className="text-2xl text-gray-600 mt-2">${product.price} (including GST)</p>
                            </div>

                            <Shows/>
                        </div>

                        {/* Right Column: Checkout Form */}
                        <div className="lg:pl-12">
                            <div className="mb-8 bg-gray-50 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                    <span className="mr-2">🎬</span> How to Watch
                                </h3>
                                <ol className="space-y-3 text-gray-700">
                                    <li className="flex">
                                        <span className="font-medium mr-2">1.</span>
                                        <span>Enter your Name and Email, then tap Buy Now</span>
                                    </li>
                                    <li className="flex">
                                        <span className="font-medium mr-2">2.</span>
                                        <span>Pay using the provided PayID and include the Reference Code</span>
                                    </li>
                                    <li className="flex">
                                        <span className="font-medium mr-2">3.</span>
                                        <span>After payment, your Viu Premium 1-Day Pass will be emailed to you</span>
                                    </li>
                                    <li className="flex">
                                        <span className="font-medium mr-2">4.</span>
                                        <span>Download the Viu app or visit <a href="https://www.viu.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">viu.com</a>, sign in, and redeem your code</span>
                                    </li>
                                </ol>
                            </div>

                            <Checkout
                                productName={product.nameEn}
                                price={product.price}
                            />

                            <div className="mt-12 pt-12 border-t border-gray-200">
                                <h3 className="text-xl font-medium text-gray-900 mb-6">Download Viu App</h3>
                                <div className="flex justify-center space-x-6">
                                    <a href="https://apps.apple.com/hk/app/viu/id1036095179?l=en" target="_blank"
                                       rel="noopener noreferrer" className="hover:opacity-80">
                                        <img src="/images/appstore-icon.webp" alt="Download on App Store"
                                             className="h-12"/>
                                    </a>
                                    <a href="https://play.google.com/store/apps/details?id=com.viu.phone&referrer=utm_source%3D"
                                       target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                                        <img src="/images/google-play-icon.webp" alt="Get it on Google Play"
                                             className="h-12"/>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="lg:hidden">
                        <div className="text-center mb-6">
                            <a href="https://www.viu.com/" target="_blank" rel="noopener noreferrer"
                               className="inline-block hover:opacity-90 transition-opacity">
                                <img src="/images/viu-logo.svg" alt="Viu Logo" className="h-8 mx-auto mb-2"/>
                            </a>
                            <img src="/images/viu-vouchers.webp" alt="Viu Voucher Banner"
                                 className="w-full h-auto rounded-lg mb-4"/>
                            <h1 className="text-xl font-bold text-gray-900">{product.nameEn}</h1>
                            <p className="text-lg text-gray-600 mt-2">${product.price} (including GST)</p>
                        </div>

                        <Shows/>

                        <div className="mt-6 mb-6 bg-gray-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                <span className="mr-2">🎬</span> How to Watch
                            </h3>
                            <ol className="space-y-2 text-sm text-gray-700">
                                <li className="flex">
                                    <span className="font-medium mr-2">1.</span>
                                    <span>Enter your Name and Email, then tap Buy Now</span>
                                </li>
                                <li className="flex">
                                    <span className="font-medium mr-2">2.</span>
                                    <span>Pay using the provided PayID and include the Reference Code</span>
                                </li>
                                <li className="flex">
                                    <span className="font-medium mr-2">3.</span>
                                    <span>After payment, your Viu Premium 1-Day Pass will be emailed to you</span>
                                </li>
                                <li className="flex">
                                    <span className="font-medium mr-2">4.</span>
                                    <span>Download the Viu app or visit <a href="https://www.viu.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">viu.com</a>, sign in, and redeem your code</span>
                                </li>
                            </ol>
                        </div>

                        <Checkout
                            productName={product.nameEn}
                            price={product.price}
                        />

                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-base font-medium text-gray-900 mb-3">Download Viu App</h3>
                            <div className="flex justify-center space-x-4">
                                <a href="https://apps.apple.com/hk/app/viu/id1036095179?l=en" target="_blank"
                                   rel="noopener noreferrer" className="hover:opacity-80">
                                    <img src="/images/appstore-icon.webp" alt="Download on App Store" className="h-10"/>
                                </a>
                                <a href="https://play.google.com/store/apps/details?id=com.viu.phone&referrer=utm_source%3D"
                                   target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                                    <img src="/images/google-play-icon.webp" alt="Get it on Google Play"
                                         className="h-10"/>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Terms and Conditions Section */}
            <section className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-m font-semibold text-gray-500 mb-4 flex items-center">
                            Terms and Conditions
                        </h2>
                        <ol className="space-y-2 text-sm text-gray-400">
                            {/*<li className="flex">*/}
                            {/*  <span className="font-medium mr-2">1.</span>*/}
                            {/*<span>Each voucher code is valid for 12 months from the date of purchase.</span>*/}
                            {/*</li>*/}
                            <li className="flex">
                                <span className="font-medium mr-2">1.</span>
                                <span>Voucher codes can only be redeemed in Viu Australia.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">2.</span>
                                <span>Voucher codes are transferable and may be shared, but can only be redeemed once.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">3.</span>
                                <span>Users may redeem multiple voucher codes on the same Viu account.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">4.</span>
                                <span>All sales are final and non-refundable. No refunds will be issued for unused or expired codes.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">5.</span>
                                <span>The voucher grants access to Viu Premium for one day per code redeemed.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">6.</span>
                                <span>Redemption requires a Viu account. Codes must be entered via the Viu app or website.</span>
                            </li>
                        </ol>
                    </div>
                </div>
            </section>

            <footer className="bg-white border-t border-gray-200 mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="text-center text-sm text-gray-600">
                        <p>© 2025 Appvanz Pty Ltd. ABN: 34668403478</p>
                        <p className="mt-1">E-mail: <a href="mailto:viu@appvanz.au" className="text-blue-600 hover:text-blue-800">viu@appvanz.au</a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}; 